import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  CustomerService,
  InceptionService,
  TranslationService,
} from '@proximus/ng-common';
import {
  ProductTypes,
  TvProduct,
  getFavoriteContentWithHighestRank,
  getTealiumLocation,
  hasEligibleFavoriteContent,
  Pack,
  Product,
} from '@dch/products-business-logic';
import { Product as UsageProduct } from '@proximus/usage-business-logic';
import { getUsageProductFrom } from 'src/app/shared/usage/util';
import { GetProducts } from 'src/app/store/actions/products.actions';
import { OnDestroyHookDirective } from '../../../../shared/hooks';
import { getPiiServiceId, savePii } from '../../../../shared/pii/pii.utils';
import { GetProductDetails } from '../../../../store/actions/product-details.actions';
import {
  ApplicationState,
  getProductDetailsPending,
  getProductDetailsTechnicalError,
  getSelectedProductDetails,
  getUserProduct,
} from '../../../../store/reducers';
import { PlcCodes } from '@dch/products-business-logic/src/shared/models/product.model';
import { EwebTokenService } from '../../../../services/eweb-token.service';

@Component({
  selector: 'mprd-tv-level-two',
  templateUrl: './tv-level-two.component.html',
})
export class TvLevelTwoComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  selectedProduct: TvProduct;
  usageProduct: UsageProduct;
  isRes: boolean;
  hasError: boolean;
  isPending: boolean;
  tealiumLocation: string;
  savePii = savePii;
  tvBonusEligible: boolean;
  remainingDaysLabel: string;
  faqHtml?: string;
  serviceId3Digits: string;

  constructor(
    private route: ActivatedRoute,
    private store: Store<ApplicationState>,
    private inceptionService: InceptionService,
    private translationService: TranslationService,
    private elementRef: ElementRef,
    private customerService: CustomerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private readonly ewebTokenService: EwebTokenService
  ) {
    super();
  }

  get isUnmigratedCustomer(): boolean {
    return this.activatedRoute.snapshot.queryParams['old'] === 'true';
  }

  get showAllAvailableOptions(): boolean {
    const isEshareTvOptionFlowEnabled =
      this.translationService.getTranslatedValueFor(
        'eshare.tv.enableOptionFlowRedirect',
      ) === 'true';

    return !isEshareTvOptionFlowEnabled || this.isUnmigratedCustomer;
  }

  ngOnInit() {
    this.addSubscription(
      this.store.select(getProductDetailsTechnicalError).subscribe((value) => {
        this.hasError = value !== null;
      }),
    );

    if (this.isADeeplink() || this.translationService.getTranslatedValueFor('eshare.tv.enableOptionFlowRedirect') === 'true') {
      this.getProducts();
    }

    if (this.isADeeplink()) {
      //getting the required params
      this.addSubscription(
        this.activatedRoute.params.subscribe((params) => {
          // here we are in the deeplink case => treating the data
          if (!!params && params['token']) {
            this.serviceId3Digits = params['token'];
          }
        }),
      );

      // if any missing info in the deeplinkCode
      if (this.serviceId3Digits && !this.hasCorrectServiceId3Digits()) {
        this.routeToOverview();
      }

      this.addSubscription(
        this.store.select(getUserProduct).subscribe((userProduct) => {
          if (
            userProduct &&
            ((userProduct.packs && userProduct.packs.length > 0) ||
              (userProduct.outOfPacks && userProduct.outOfPacks.length > 0))
          ) {
            const productsAvailableForDeepLink =
              this.getProductsAvailableForDeepLink(userProduct);

            if (
              !productsAvailableForDeepLink ||
              productsAvailableForDeepLink.length !== 1
            ) {
              this.routeToOverview();
            } else {
              this.loadSelectedTvProductDetails(
                productsAvailableForDeepLink[0].accessNumber,
              );
            }
          }
        }),
      );
    } else {
      this.loadTvProductDetails();
    }

    this.scrollToTop();

    this.translationService.parse(this.elementRef.nativeElement);

    const url = 'tv-details-faq%7EmgnlArea=body%7E.html';
    this.http.get(url, { responseType: 'text' }).subscribe((response) => {
      this.faqHtml = this.sanitizer.sanitize(SecurityContext.HTML, response);
    });
  }

  private getProductsAvailableForDeepLink(userProduct: any): TvProduct[] {
    return this.serviceId3Digits
      ? this.filterTVProductsByServiceId(
          userProduct.packs,
          userProduct.outOfPacks,
          this.serviceId3Digits,
        )
      : this.filterTVProducts(userProduct.packs);
  }

  loadSelectedTvProductDetails(accessNumber: string) {
    savePii(accessNumber);
    this.loadTvProductDetails();
  }

  loadTvProductDetails() {
    this.addSubscription(
      this.route.params.subscribe((params) => {
        this.store.dispatch(
          new GetProductDetails({
            productType: ProductTypes.TV.toString(),
            serviceId: getPiiServiceId(),
          }),
        );
      }),
    );

    this.addSubscription(
      this.store.select(getSelectedProductDetails).subscribe((value) => {
        if (value && value.tvProductDetails) {
          this.selectedProduct = value.tvProductDetails;
          this.usageProduct = getUsageProductFrom(this.selectedProduct);
          this.tealiumLocation = getTealiumLocation(
            this.selectedProduct as any,
          );
          this.tvBonusEligible =
            this.selectedProduct &&
            (hasEligibleFavoriteContent(this.selectedProduct) ||
              !!getFavoriteContentWithHighestRank(this.selectedProduct));
        }
      }),
    );

    this.addSubscription(
      this.customerService.getCustomerAccount().subscribe((value) => {
        this.isRes =
          value &&
          (value.getSelectedCustomer().subsegment === 'RES' ||
            value.getSelectedCustomer().subsegment === 'PRE');
      }),
    );

    this.addSubscription(
      this.store.select(getProductDetailsPending).subscribe((pending) => {
        this.isPending = pending;
      }),
    );
  }

  filterTVProductsByServiceId(
    packs: any,
    outOfPacks: any,
    serviceId3Digits: string,
  ): TvProduct[] {
    const tvProducts: TvProduct[] = [];
    packs.forEach((pack) => {
      const coreProducts = pack?.coreProducts;
      const tvProduct: Array<TvProduct> = coreProducts.filter(
        (product) =>
          product &&
          product.plc === 'TV' &&
          product.accessNumber.endsWith(serviceId3Digits),
      );
      tvProducts.push(...tvProduct);
    });

    outOfPacks.forEach((outOfpack) => {
      const coreProducts = outOfpack?.coreProducts;
      const tvProduct: Array<TvProduct> = coreProducts.filter(
        (product) =>
          product &&
          product.plc === 'TV' &&
          product.accessNumber.endsWith(serviceId3Digits),
      );
      tvProducts.push(...tvProduct);
    });
    return tvProducts;
  }

  // We receive the information from the business that we need to check only product TV InPack and not in the outPack
  // Proximus does not sell TV products outside pack
  private filterTVProducts(packs: Pack[]): TvProduct[] {
    const tvProducts: TvProduct[] = [];
    packs.forEach((pack) => {
      const coreProducts = pack?.coreProducts;
      const tvProduct = coreProducts.filter(
        (product) => product && product.plc === PlcCodes.TV,
      ) as Array<TvProduct>;
      tvProducts.push(...tvProduct);
    });

    return tvProducts;
  }

  routeToOverview() {
    this.router.navigate(['/overview']);
  }

  private getProducts() {
    this.store.dispatch(new GetProducts());
  }

  hasCorrectServiceId3Digits(): boolean {
    //check serviceId3Digits has 3 digits
    const regex = /\d{3}/;
    return regex.test(this.serviceId3Digits);
  }

  private isADeeplink() {
    return this.router.url.includes('/deeplink');
  }

  getServiceIdFromDeepLink(): string {
    return '';
  }

  usageInitializedWith(remainingDaysLabel: string) {
    if (remainingDaysLabel) {
      this.remainingDaysLabel = remainingDaysLabel;
    }
  }

  onClickPersonalizedOptionsCtaBanner() {
    this.addSubscription(this.ewebTokenService.getRedirectToEshareOptionFlowUrl(
      PlcCodes.TV,
      null,
      getPiiServiceId()
    ).subscribe((redirectUrl: string) => {
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }));
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.inceptionService.scrollToElement('#tvLevelTwo');
    });
  }
}
